import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  useMediaQuery,
  useTheme
} from '@mui/material';
import UserManagement from './UserManagement';
import InvitationManagement from './InvitationManagement';
import PromptManagement from './PromptManagement';
import AppCustomization from './AppCustomization';
import { AdminPanelSettings, People, Email, TextSnippet, Palette } from '@mui/icons-material';
import { useUser } from '@clerk/clerk-react';
import { useInstanceRole } from '../../hooks/useInstanceRole';
import { useAppSettings } from '../../contexts/AppSettingsContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const AdminDashboard: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const { user, isLoaded: isUserLoaded } = useUser();
  const { settings } = useAppSettings();
  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.down('sm'));

  // Use instance-specific role hook for admin verification
  const { isAdmin, loading, error } = useInstanceRole();

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  if (loading || !isUserLoaded) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAdmin) {
    return (
      <Container maxWidth="md" sx={{ py: 6, textAlign: 'center' }}>
        <Typography variant="h4" color="error">Access Denied</Typography>
        <Typography variant="body1" sx={{ mt: 2 }}>
          You do not have administrative privileges to access this page.
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mt: 2, mx: 'auto', maxWidth: 'md' }}>
            {error}
          </Alert>
        )}
      </Container>
    );
  }

  return (
    <Container 
      maxWidth="xl" 
      sx={{ 
        py: { xs: 2, sm: 3, md: 4 }, 
        px: { xs: 1, sm: 2, md: 3 },
      }}
    >
      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ 
          p: { xs: 2, sm: 3 }, 
          display: 'flex', 
          alignItems: 'center', 
          backgroundColor: settings.primaryColor || '#5D5FEF', 
          color: 'white' 
        }}>
          <AdminPanelSettings sx={{ fontSize: { xs: 24, sm: 28, md: 32 }, mr: { xs: 1, sm: 2 } }} />
          <Typography 
            variant={isMobileView ? "h5" : "h4"} 
            fontWeight="bold"
            sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' } }}
          >
            Admin Portal
          </Typography>
        </Box>
        
        <Divider />
        
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            aria-label="admin tabs"
            variant={isMobileView ? "scrollable" : "standard"}
            scrollButtons={isMobileView ? "auto" : false}
            orientation={isMobileView ? "horizontal" : "horizontal"}
            allowScrollButtonsMobile
            sx={{ 
              '& .MuiTab-root': { 
                py: { xs: 1.5, sm: 2 },
                px: { xs: 1, sm: 2 },
                fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
                fontWeight: 500,
                minWidth: { xs: 'auto', sm: 120 }
              },
              '& .Mui-selected': {
                color: settings.primaryColor || '#5D5FEF'
              },
              '& .MuiTabs-indicator': {
                backgroundColor: settings.primaryColor || '#5D5FEF'
              }
            }}
          >
            <Tab 
              icon={<People sx={{ mr: { xs: 0.5, sm: 1 }, fontSize: { xs: '1.25rem', sm: 'inherit' } }} />} 
              label={!isMobileView ? "User Management" : "Users"} 
              iconPosition="start" 
            />
            <Tab 
              icon={<Email sx={{ mr: { xs: 0.5, sm: 1 }, fontSize: { xs: '1.25rem', sm: 'inherit' } }} />} 
              label={!isMobileView ? "Invitations" : "Invites"} 
              iconPosition="start" 
            />
            <Tab 
              icon={<TextSnippet sx={{ mr: { xs: 0.5, sm: 1 }, fontSize: { xs: '1.25rem', sm: 'inherit' } }} />} 
              label="Prompts" 
              iconPosition="start" 
            />
            <Tab 
              icon={<Palette sx={{ mr: { xs: 0.5, sm: 1 }, fontSize: { xs: '1.25rem', sm: 'inherit' } }} />} 
              label={!isMobileView ? "Customization" : "Custom"} 
              iconPosition="start" 
            />
          </Tabs>
        </Box>
        
        <TabPanel value={tabValue} index={0}>
          <UserManagement />
        </TabPanel>
        
        <TabPanel value={tabValue} index={1}>
          <InvitationManagement />
        </TabPanel>
        
        <TabPanel value={tabValue} index={2}>
          <PromptManagement />
        </TabPanel>
        
        <TabPanel value={tabValue} index={3}>
          <AppCustomization />
        </TabPanel>
      </Paper>
    </Container>
  );
};

export default AdminDashboard; 